import {
  ChecklistItem,
  CursorDirection,
  CursorPagingOptions,
  GoalOption,
  ImpactTile,
  PublicProject,
  PublicProjectSummary,
  ReferralImpact,
  Tile,
  User,
  UserType,
} from "@ecologi/shared";
import Rotator from "exif-auto-rotate";
import { useRouter } from "next/router";
import NProgress from "nprogress";
import { useCallback, useMemo } from "react";
import { QueryCache, QueryConfig, useMutation, useQuery } from "react-query";
import { dehydrate } from "react-query/hydration";
import { toast, ToastId } from "react-toastify";
import {
  APIError,
  formatAPIErrorForView,
  getAllProjects,
  getAggregateOfCarbonOffsetsByProject,
  getAggregateOfCarbonRemovalsByProject,
  getAggregateOfHabitatRestorationsByProject,
  getProfileByUsernameOrId,
  getProjectById,
  getReferralImpact,
  getTile,
  getUserTilesPaginated,
  updateUser,
  uploadProfileImage,
} from "../../../lib";
import { getPendingImpactTotals } from "../../../lib/api/impact-order-reports";
import { useStore } from "../../../store";

const pageinatedTilesKey = "paginated-tiles";
const profileKey = "profile";
const referralKey = "referral";
const projectKey = "project";

// SSR -----------------------------------------------------------------------

export type ProfileSSRParams = {
  usernameOrId: string;
  childUsernameOrId?: string;
};

export type ProfileSSRProps = {
  accessToken?: string;
  allProjects: PublicProjectSummary[];
  layoutProps: {
    isProfile: boolean;
  };
  profile: UserType | null;
};

/**
 * Fetch a public profile and the project metadata needed for SSR load.
 */
export const prefetchData = async (
  params: ProfileSSRParams,
  accessToken?: string
): Promise<ProfileSSRProps> => {
  const [profile, allProjects] = await Promise.all([
    prefetchProfileForSSR(params, accessToken),
    getAllProjects(),
  ]);

  return {
    // Must be null because undefined is not serialisable in Next SSR
    profile: profile || null,
    allProjects,
    layoutProps: {
      isProfile: true,
    },
  };
};

/**
 * Returns a public profile for SSR
 */
export const prefetchProfileForSSR = async (
  params: ProfileSSRParams,
  accessToken?: string
): Promise<UserType | undefined> => {
  const queryCache = new QueryCache();

  const headers = accessToken
    ? { Authorization: `Bearer ${accessToken}` }
    : undefined;

  await queryCache.prefetchQuery(
    [profileKey, params.usernameOrId, params.childUsernameOrId],
    () =>
      getProfileByUsernameOrId(
        null,
        params.usernameOrId,
        params.childUsernameOrId,
        headers
      )
  );

  return dehydrate(queryCache).queries?.[0]?.data as UserType;
};

// --------------------------------------------------------------------------

export const useQueryReferralImpact = (profile: User) => {
  return useQuery<ReferralImpact, APIError>(
    [referralKey, profile.userId, profile.referralImpactIncluded],
    getReferralImpact,
    {
      enabled: !!profile.referralImpactIncluded,
    }
  );
};

export const usePaginatedTiles = (
  userId: string,
  take: number,
  cursor?: CursorPagingOptions,
  includePending = false
) =>
  useQuery<{ data: Tile[]; direction: CursorDirection }, APIError>(
    [pageinatedTilesKey, userId, take, cursor, includePending],
    async (_, userId, take, cursor, includePending) => {
      try {
        const data = await getUserTilesPaginated(
          _,
          userId,
          take,
          cursor,
          includePending
        );

        // direction is useful for knowing if to insert at head or tail of tree list
        return { data, direction: cursor.direction };
      } catch (error) {
        throw error;
      }
    },
    { enabled: !!cursor }
  );

export const useQueryAggregateOfCarbonOffsetsByProject = (userId: string) =>
  useQuery(
    `aggregate-of-carbon-offsets-by-project-for-${userId}`,
    () => getAggregateOfCarbonOffsetsByProject(userId),
    {
      enabled: !!userId,
    }
  );

export const useQueryAggregateOfCarbonRemovalsByProject = (userId: string) =>
  useQuery(
    `aggregate-of-carbon-removals-by-project-for-${userId}`,
    () => getAggregateOfCarbonRemovalsByProject(userId),
    {
      enabled: !!userId,
    }
  );

export const useQueryAggregateOfHabitatRestorationsByProject = (
  userId: string
) =>
  useQuery(
    `aggregate-of-habitat-restorations-by-project-for-${userId}`,
    () => getAggregateOfHabitatRestorationsByProject(userId),
    {
      enabled: !!userId,
    }
  );

export const useQueryPendingImpactTotals = (userId: string) =>
  useQuery(
    `pending-impact-totals-for-${userId}`,
    () => getPendingImpactTotals(userId),
    {
      enabled: !!userId,
    }
  );

export const useQueryProfile = (
  rootUsernameOrId,
  childUsernameOrId = undefined
) => {
  const router = useRouter();
  const { user: myself } = useStore();
  const isOwnProfile: boolean =
    myself &&
    [myself.userId, myself.username].includes(
      childUsernameOrId ? childUsernameOrId : rootUsernameOrId
    );

  const onError = (error) => {
    if (APIError.isNotFoundError(error)) {
      router.push(`/404`);
    }
  };

  // Lazy eval means we don't call API when fetching our own profile from the store
  return !isOwnProfile
    ? useQuery<UserType, APIError>(
        [profileKey, rootUsernameOrId, childUsernameOrId],
        getProfileByUsernameOrId,
        {
          onError,
          retry: false, // onError is not called unless this is false
          refetchOnWindowFocus: false,
        }
      )
    : // Workaround to expose the user as a react-query result type.
      useQuery<User, APIError>(
        ["get-store-user-as-result"],
        async (_) => myself,
        {
          onError,
          retry: false, // onError is not called unless this is false
        }
      );
};

export const useUploadFileImage = () => {
  const { setUser, user } = useStore();

  // Keep the in progress toast up for as long as the response is being awaited
  const inProgressToastId: ToastId = "FILE_UPLOAD_IN_PROGRESS";

  const [uploadProfileImageMutation] = useMutation(uploadProfileImage, {
    onMutate: () => {
      NProgress.start();

      // Prevent duplicate toasts from showing up
      if (!toast.isActive(inProgressToastId)) {
        toast.info("Upload started, please wait...", {
          toastId: inProgressToastId,
          autoClose: false,
        });
      }
    },
    onSuccess: async ({ user }) => {
      setUser(new User(user));
      toast.success("Profile image updated");
    },
    onError: (error: APIError) => {
      toast.error(formatAPIErrorForView(error));
    },
    onSettled: () => {
      NProgress.done();
      toast.dismiss(inProgressToastId);
    },
  });

  return useCallback(
    async (file: Blob | string) => {
      // extract EXIF rotation data & correct so that phone pictures are uploaded
      // in the correct orientation
      if (
        typeof file == "string" &&
        // only for JPEGs since that's what iPhones take
        (file.startsWith("data:image/jpeg;") ||
          file.startsWith("data:image/jpg;"))
      ) {
        // handling a base64 JPEG image string, need to convert to a Blob to rotate
        const bytes = Uint8Array.from(
          atob(file)
            .split("")
            .map((char) => char.charCodeAt(0))
        );

        file = new Blob([bytes], { type: file.split("data:")[0] });
      }

      try {
        // attempt to scrub exif rotation data (if the image has any)
        if (
          file instanceof Blob &&
          ["image/jpg", "image/jpeg"].includes(file.type)
        )
          file = await Rotator.createRotatedImageAsync(file as Blob, "blob");
      } catch (error) {
        // library throws if no EXIF, we can safely ignore this error though
      }

      await uploadProfileImageMutation({
        userId: user.userId,
        file: file as Blob,
      });
    },
    [uploadProfileImageMutation, user]
  );
};

export const useEditForestTitle = () => {
  const { setUser } = useStore();

  const [updateForestTitle] = useMutation(updateUser, {
    onSuccess: ({ user }) => {
      setUser(new User(user));
    },
    onError: (error: APIError) => {
      toast.error(formatAPIErrorForView(error));
    },
  });

  return updateForestTitle;
};

export const useChecklistItemHandlers = () => {
  const { setUser } = useStore();

  const [updateUserMutation] = useMutation(updateUser, {
    onSuccess: ({ user }) => {
      setUser(new User(user));
    },
    onError: (error: APIError) => {
      toast.error(formatAPIErrorForView(error));
    },
  });

  return useMemo(
    () => ({
      handleAddChecklistItem: async (
        user: User,
        goalOption: GoalOption,
        cb?: () => void
      ) => {
        NProgress.start();
        await updateUserMutation({
          userId: user.userId,
          fieldsToUpdate: {
            checklistItems: [
              ...user.checklistItems,
              {
                _id: goalOption.value,
                text: goalOption.label,
                isComplete: false,
              },
            ],
          },
        });
        cb && cb();
        NProgress.done();
      },
      handleRemoveChecklistItem: async (user: User, item: ChecklistItem) => {
        // Find the index for this item
        const itemIdx = user.checklistItems.findIndex(
          (i) => i._id === item._id
        );

        // Splice it in into the users checkout list items
        // (splice mutates so we expand out the original)
        const checklistItems = [...user.checklistItems];
        checklistItems.splice(itemIdx, 1);

        NProgress.start();
        await updateUserMutation({
          userId: user.userId,
          fieldsToUpdate: {
            checklistItems,
          },
        });
        NProgress.done();
      },

      handleToggleChecklistItem: async (user: User, item: ChecklistItem) => {
        // Find the index for this item
        const itemIdx = user.checklistItems.findIndex(
          (i) => i._id === item._id
        );

        // Create a new item so we're not mutating things
        const updatedItem = {
          ...item,
          isComplete: !item.isComplete,
        };

        // Splice it in into the users checkout list items
        // (splice mutates so we expand out the original)
        const checklistItems = [...user.checklistItems];
        checklistItems.splice(itemIdx, 1, updatedItem);

        NProgress.start();
        await updateUserMutation({
          userId: user.userId,
          fieldsToUpdate: {
            checklistItems,
          },
        });
        NProgress.done();
      },
    }),
    [updateUserMutation]
  );
};

export const useQueryProject = (
  projectId?: number,
  queryConfig?: QueryConfig<PublicProject>
) =>
  useQuery<PublicProject, APIError>(
    [projectKey, projectId],
    () => {
      if (projectId === undefined) {
        throw new Error("Project ID is required");
      }

      return getProjectById(projectId);
    },
    queryConfig
  );

export const useFetchTile = (
  userId: string,
  tileId: string,
  queryConfig?: QueryConfig<ImpactTile>
) =>
  useQuery<ImpactTile, APIError>(
    [userId, tileId],
    () => getTile(userId, tileId),
    queryConfig
  );
